import React from 'react';
import {
  UsersIcon,
  DocumentChartBarIcon,
  Cog6ToothIcon,
  ShieldCheckIcon,
  CurrencyDollarIcon,
  QrCodeIcon,
  ArchiveBoxIcon
} from '@heroicons/react/24/outline';

const AdminDashboardPage = () => {
  // Mock data for the dashboard
  const stats = [
    { name: 'Active Users', value: '42', change: '+8%', icon: UsersIcon, color: 'indigo' },
    { name: 'Items Scanned', value: '1,284', change: '+12%', icon: QrCodeIcon, color: 'primary' },
    { name: 'Monthly Revenue', value: '$8,942', change: '+5%', icon: CurrencyDollarIcon, color: 'green' },
    { name: 'Storage Used', value: '62%', change: '+3%', icon: ArchiveBoxIcon, color: 'blue' }
  ];
  
  const recentActivity = [
    { user: 'John Doe', action: 'Added 23 new items', time: '12 minutes ago' },
    { user: 'Jane Smith', action: 'Updated inventory status', time: '1 hour ago' },
    { user: 'Mike Johnson', action: 'Exported sales report', time: '3 hours ago' },
    { user: 'Sarah Williams', action: 'Created new list', time: '5 hours ago' },
    { user: 'Robert Brown', action: 'Updated account settings', time: 'Yesterday' }
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <div className="flex space-x-2">
          <button className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500">
            Export Reports
          </button>
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500">
            Settings
          </button>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat, index) => (
          <div key={index} className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className={`flex-shrink-0 bg-${stat.color}-50 rounded-md p-3 mr-4`}>
                <stat.icon className={`h-6 w-6 text-${stat.color}-700`} aria-hidden="true" />
              </div>
              <div>
                <p className="text-gray-500 text-sm">{stat.name}</p>
                <p className="text-2xl font-bold mt-1">{stat.value}</p>
                <p className="text-green-500 text-sm mt-1">{stat.change} from last month</p>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* User Activity */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-lg">Recent Activity</h2>
          </div>
          <div className="p-6">
            <ul className="divide-y divide-gray-200">
              {recentActivity.map((activity, index) => (
                <li key={index} className="py-4 first:pt-0 last:pb-0">
                  <div className="flex space-x-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                        {activity.user.charAt(0)}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">{activity.user}</p>
                      <p className="text-sm text-gray-500">{activity.action}</p>
                      <p className="text-xs text-gray-400">{activity.time}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            <div className="mt-4 text-center">
              <button className="text-primary-600 hover:text-primary-800 text-sm font-medium">
                View All Activity
              </button>
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="font-semibold text-lg">Quick Actions</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 bg-indigo-50 rounded-md p-2">
                    <UsersIcon className="h-6 w-6 text-indigo-700" aria-hidden="true" />
                  </div>
                  <span>Manage Users</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 bg-blue-50 rounded-md p-2">
                    <DocumentChartBarIcon className="h-6 w-6 text-blue-700" aria-hidden="true" />
                  </div>
                  <span>Generate Reports</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 bg-green-50 rounded-md p-2">
                    <Cog6ToothIcon className="h-6 w-6 text-green-700" aria-hidden="true" />
                  </div>
                  <span>System Settings</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
              
              <button className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-md transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0 bg-purple-50 rounded-md p-2">
                    <ShieldCheckIcon className="h-6 w-6 text-purple-700" aria-hidden="true" />
                  </div>
                  <span>Security Audit</span>
                </div>
                <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;
